import type {ReactElement} from 'react';
import {useCallback, useContext, useEffect, useState} from 'react';
import {Button, ButtonSize} from '@Components/button';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {CUSTOM_EVENT_COMPONENT_STYLE_COPIED} from '@EmailMakerReact/email-maker-react.constants';
import {hasCopiedComponentStyles} from '@EmailMakerReact/lib/email-maker-clipboard-library';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import type {Editor} from 'grapesjs';
import type {SingleButtonProps} from './single-cta-button.types';
import styles from '../../cta-button-control.module.scss';

/** Disabled until a component's styles are copied, then enabled for the rest of the session. */
function useStylesCopiedGate(editor: Editor | null, gated: boolean | undefined): boolean {
  const [hasCopied, setHasCopied] = useState<boolean>(() => (editor ? hasCopiedComponentStyles(editor) : false));

  useEffect((): (() => void) | undefined => {
    if (!gated) {
      return undefined;
    }
    const onCopied = (): void => {
      setHasCopied(true);
    };
    document.addEventListener(CUSTOM_EVENT_COMPONENT_STYLE_COPIED, onCopied);
    return (): void => {
      document.removeEventListener(CUSTOM_EVENT_COMPONENT_STYLE_COPIED, onCopied);
    };
  }, [gated]);

  return Boolean(gated) && !hasCopied;
}

export function SingleCtaButton({btn, component}: SingleButtonProps): ReactElement {
  const {editor} = useContext(emailMakerContext);
  const isDisabled = useStylesCopiedGate(editor, btn.disabledUntilStylesCopied);

  const handleClick = useCallback((): void => {
    if (!editor) {
      return;
    }
    btn.cb(editor, component);
  }, [editor, component, btn]);

  const label = window.i18next.t(btn.textKey);
  const icon = btn.iconClass ? <Icon aria-hidden icon={btn.iconClass} size={IconSize.SIZE_ICON_20} /> : undefined;

  return (
    <Button
      customClasses={styles.ctaBtn}
      customIcon={icon}
      disabled={isDisabled}
      iconPlacement={btn.iconPlacement}
      onClick={handleClick}
      size={ButtonSize.MEDIUM}
      text={label}
      title={label}
      type={btn.type}
    />
  );
}
